/**
 * Modular exponentiation via square-and-multiply (MSB-first).
 *
 * Written as a generator: each `yield` is a meaningful pause point.
 * The animation framework iterates this generator to materialize a
 * step list — the algorithm itself is the source of truth, no parallel
 * data structure to maintain.
 *
 * Iteration scheme: scan the binary expansion of the exponent from the
 * most significant bit down. At each bit:
 *   1. Square the running `result` (always)
 *   2. If the bit is 1, multiply `result` by `base`
 *
 * (Note: `power` is kept around for display continuity but is not used
 * in MSB-first scanning the way it would be in LSB-first. We track the
 * "next square" purely as `result^2` so the visual has a stable second
 * state to show. For LSB-first scanning we'd track `power = base^(2^i)`
 * separately, but MSB-first is cleaner to animate.)
 */

import type { ModexpStep, ModexpInputs } from './types';

export function* repeatedSquaring({ base, exp, mod }: ModexpInputs): Generator<ModexpStep> {
  yield { kind: 'start', base, exp, mod };

  if (exp === 0n) {
    yield { kind: 'done', result: 1n % mod };
    return;
  }

  const bits = exp.toString(2);
  yield { kind: 'binary', bits };

  let result = 1n;
  // First bit (MSB) — initialize without squaring (1^2 = 1, no point showing it).
  const firstBit = bits[0];
  if (firstBit === '1') {
    result = base % mod;
    yield {
      kind: 'inspect-bit',
      bitIndex: 0,
      bit: '1',
      result,
      power: result,
    };
  }

  // Subsequent bits (square always, multiply if 1)
  for (let i = 1; i < bits.length; i++) {
    const bitChar = bits[i];
    const bit: '0' | '1' = bitChar === '1' ? '1' : '0';

    yield {
      kind: 'inspect-bit',
      bitIndex: i,
      bit,
      result,
      power: result,
    };

    // Square step
    const previousResult = result;
    result = (result * result) % mod;
    yield {
      kind: 'square',
      bitIndex: i,
      bit,
      previousResult,
      result,
      power: result,
    };

    // Multiply step (only if bit is 1)
    if (bit === '1') {
      const beforeMul = result;
      result = (result * base) % mod;
      yield {
        kind: 'multiply',
        bitIndex: i,
        bit: '1',
        previousResult: beforeMul,
        result,
        power: result,
      };
    }
  }

  yield { kind: 'done', result };
}
